import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { Icon } from "@/components/common/Icon";
import { SectionLabel, CtaBanner } from "@/pages/Home";
import { PRODUCTS } from "@/data/products";
import { SERVICES } from "@/data/services";

export default function Solutions() {
  const { lang, t, ml } = useI18n();
  const L = (p) => `/${lang}${p}`;
  useSEO({
    lang,
    title: `${t("solutionsPage.label")} | HO-SYS Advanced Technologies`,
    description: t("solutionsPage.subtitle"),
    path: "/solutions",
  });

  return (
    <div className="pt-32">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <SectionLabel>{t("solutionsPage.label")}</SectionLabel>
          <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.02] max-w-4xl">
            {t("solutionsPage.title")}
          </h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl max-w-2xl">
            {t("solutionsPage.subtitle")}
          </p>
        </Reveal>
      </section>

      {/* Product lines (Ecosystem) */}
      <section
        data-testid="solutions-products"
        className="max-w-7xl mx-auto px-5 sm:px-8 pb-16"
      >
        <Reveal>
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <SectionLabel>{t("solutionsPage.productsLabel")}</SectionLabel>
              <h2 className="mt-3 font-display font-bold text-white text-3xl md:text-4xl">
                {t("solutionsPage.productsTitle")}
              </h2>
            </div>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.05}>
              <Link
                to={L(`/products/${p.slug}`)}
                data-testid={`solutions-product-${p.slug}`}
                className="group block h-full"
              >
                <motion.div
                  whileHover={{ y: -4 }}
                  className="relative h-full rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 overflow-hidden hover:border-neon/40 transition-colors"
                >
                  <div
                    className="absolute -top-16 -right-10 w-40 h-40 rounded-full blur-3xl opacity-20"
                    style={{ background: p.accent }}
                  />
                  <div
                    className="relative w-12 h-12 rounded-xl flex items-center justify-center mb-4 border"
                    style={{
                      borderColor: `${p.accent}66`,
                      background: `${p.accent}1A`,
                    }}
                  >
                    <Icon name={p.icon} className="w-6 h-6" style={{ color: p.accent }} />
                  </div>
                  <h3 className="relative font-display text-xl font-semibold text-white group-hover:text-neon transition-colors">
                    {p.name}
                  </h3>
                  <p className="relative mt-2 text-white/55 text-sm leading-relaxed">
                    {ml(p.tagline)}
                  </p>
                  <span
                    className="relative mt-4 inline-flex items-center gap-1.5 text-xs font-semibold"
                    style={{ color: p.accent }}
                  >
                    {t("common.explore")}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </motion.div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services (Capabilities) */}
      <section
        data-testid="solutions-services"
        className="max-w-7xl mx-auto px-5 sm:px-8 pb-24"
      >
        <Reveal>
          <div className="mt-4 mb-8">
            <SectionLabel>{t("solutionsPage.servicesLabel")}</SectionLabel>
            <h2 className="mt-3 font-display font-bold text-white text-3xl md:text-4xl">
              {t("solutionsPage.servicesTitle")}
            </h2>
            <p className="mt-3 text-white/55 max-w-2xl">
              {t("solutionsPage.servicesSubtitle")}
            </p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.05}>
              <Link
                to={L(`/services/${s.slug}`)}
                data-testid={`solutions-service-${s.slug}`}
                className="group block h-full"
              >
                <motion.div
                  whileHover={{ y: -4 }}
                  className="h-full rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-neon/40 transition-colors"
                >
                  <div className="w-11 h-11 rounded-xl bg-neon/10 border border-neon/25 flex items-center justify-center mb-4">
                    <Icon name={s.icon} className="w-5 h-5 text-neon" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white group-hover:text-neon transition-colors">
                    {ml(s.title)}
                  </h3>
                  <p className="mt-2 text-white/55 text-sm leading-relaxed line-clamp-3">
                    {ml(s.description)}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-neon">
                    {t("common.readMore")}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </motion.div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
